import { MutableRefObject, useEffect, useRef } from 'react';

interface UseClickOutside {
	(
		ref: MutableRefObject<HTMLElement | null>,
		handler: (event: MouseEvent) => void,
		params?: { extraDoc?: Document | null; capture?: boolean; disabled?: boolean }
	): void;
}

export const useClickOutside: UseClickOutside = (ref, handler, params = {}) => {
	// Decided when the press starts: a click may remove the element it pressed, such as a list row's remove button
	const mouseDownOutsideRef = useRef(false);
	const capture = params.capture ?? false;
	const disabled = params.disabled ?? false;

	useEffect(() => {
		const mouseDownListener = (event: MouseEvent) => {
			mouseDownOutsideRef.current = !!ref.current && !ref.current.contains(event.target as Node);
		};

		const clickListener = (event: MouseEvent) => {
			if (handler && ref.current && mouseDownOutsideRef.current) {
				handler(event);
			}
			mouseDownOutsideRef.current = false;
		};

		const docs = [document, ...(params.extraDoc ? [params.extraDoc] : [])];
		const controller = new AbortController();

		if (!disabled) {
			docs.forEach(doc => {
				doc.addEventListener('mousedown', mouseDownListener, { capture, signal: controller.signal });
				doc.addEventListener('click', clickListener, { capture, signal: controller.signal });
			});
		}

		return () => controller.abort();
	}, [ref, handler, params.extraDoc, capture, disabled]);

	return null;
};
